import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import FlowDesigner from "@/components/FlowDesigner";
import { Megaphone, Plus, PencilSimple, Copy, Trash, FlowArrow } from "@phosphor-icons/react";

export default function GlobalTemplates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "" });
  const [saving, setSaving] = useState(false);
  const [designer, setDesigner] = useState(null); // {template, flow}
  const [savingFlow, setSavingFlow] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/templates")).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Enter a name"); return; }
    setSaving(true);
    try { await api.post("/templates", form); toast.success("Template created"); setOpen(false); setForm({ name: "", description: "", category: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const duplicate = async (t) => { try { await api.post(`/templates/${t.id}/duplicate`); toast.success("Duplicated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/templates/${t.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePublish = async (t) => { try { await api.put(`/templates/${t.id}`, { status: t.status === "published" ? "draft" : "published" }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const openDesigner = async (t) => {
    const { data } = await api.get(`/templates/${t.id}`);
    setDesigner({ template: data, flow: data.flow || { nodes: [], edges: [] } });
  };
  const saveFlow = async (flow, opts = {}) => {
    setSavingFlow(true);
    try {
      await api.put(`/templates/${designer.template.id}`, { flow, draft: !!opts.draft });
      toast.success(opts.keepOpen ? "Draft saved — keep editing" : "Flow saved");
      if (opts.keepOpen) { setDesigner((d) => (d ? { ...d, flow } : d)); }
      else { setDesigner(null); load(); }
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingFlow(false); }
  };

  return (
    <div>
      <PageHeader title="Global Campaigns" subtitle="Reusable node/flow campaign templates customers can adopt">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="create-template-btn" className="rounded-lg"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> New Template</Button></DialogTrigger>
          <DialogContent data-testid="create-template-dialog">
            <DialogHeader><DialogTitle>Create global campaign template</DialogTitle>
              <DialogDescription>Build the call flow visually after creating it.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Name</Label><Input data-testid="template-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sales Qualification" /></div>
              <div className="space-y-2"><Label>Category</Label><Input data-testid="template-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Sales" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea data-testid="template-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} data-testid="submit-template-btn" className="rounded-lg">{saving ? "Creating…" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? <EmptyState testid="templates-empty" icon={Megaphone} title="No templates yet" description="Create your first global campaign template and design its call flow." />
          : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="templates-grid">
            {rows.map((t) => (
              <div key={t.id} className="rounded-xl border border-border bg-card card-shadow p-6 flex flex-col" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    {t.category && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent text-primary">{t.category}</span>}
                    <h3 className="font-semibold text-lg mt-2">{t.name}</h3>
                  </div>
                  <button onClick={() => togglePublish(t)} data-testid={`publish-toggle-${t.id}`}
                    className={`text-xs px-2 py-0.5 rounded-full border ${t.status === "published" ? "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.25)]" : "bg-muted text-muted-foreground border-border"}`}>{t.status}</button>
                </div>
                <p className="text-sm text-muted-foreground mt-2 flex-1 line-clamp-3">{t.description}</p>
                <p className="text-xs text-muted-foreground mt-3">{(t.flow?.nodes?.length || 0)} nodes · {(t.flow?.edges?.length || 0)} connections</p>
                <div className="flex items-center gap-1.5 mt-4">
                  <Button size="sm" className="rounded-lg flex-1" onClick={() => openDesigner(t)} data-testid={`design-template-${t.id}`}><FlowArrow className="w-4 h-4 mr-1.5" weight="bold" /> Design</Button>
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => duplicate(t)} data-testid={`duplicate-template-${t.id}`}><Copy className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => del(t)} data-testid={`delete-template-${t.id}`}><Trash className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>}

      <Dialog open={!!designer} onOpenChange={(v) => !v && setDesigner(null)}>
        <DialogContent className="max-w-[95vw] w-[95vw]" data-testid="designer-dialog">
          <DialogHeader><DialogTitle>Campaign Designer — {designer?.template?.name}</DialogTitle>
            <DialogDescription>Drag nodes, connect the call flow. Changes save to this template only.</DialogDescription></DialogHeader>
          {designer && <FlowDesigner initial={designer.flow} onSave={saveFlow} saving={savingFlow} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
